import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Calendar, ClipboardCheck, Clock,
  MapPin, User, ChevronDown, ChevronUp, CalendarDays, DollarSign,
  AlertCircle
} from 'lucide-react';

const DAY_MS = 86400000;
const CONTRACTOR_COLORS = {};
let colorIdx = 0;
const PALETTE = ['#D35400', '#2563EB', '#7C3AED', '#0891B2', '#DC2626', '#059669', '#D97706', '#6366F1', '#0D9488', '#BE185D'];

function getContractorColor(name) {
  if (!name) return '#94A3B8';
  if (!CONTRACTOR_COLORS[name]) {
    CONTRACTOR_COLORS[name] = PALETTE[colorIdx % PALETTE.length];
    colorIdx++;
  }
  return CONTRACTOR_COLORS[name];
}

function addDays(dateStr, days) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function daysBetween(a, b) {
  return Math.round((new Date(b + 'T00:00:00') - new Date(a + 'T00:00:00')) / DAY_MS);
}

const STATUS_BADGE = {
  'Draft': 'bg-slate-100 text-slate-600', 'Offered': 'bg-amber-100 text-amber-700',
  'Accepted': 'bg-blue-100 text-blue-700', 'Scheduled': 'bg-purple-100 text-purple-700',
  'In Progress': 'bg-orange-100 text-orange-700', 'Completed': 'bg-green-100 text-green-700',
};

export default function GanttChartPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [bookedAppraisals, setBookedAppraisals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [gstDates, setGstDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewStart, setViewStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 3);
    return d.toISOString().slice(0, 10);
  });
  const [viewDays, setViewDays] = useState(28);
  const [expandedJobs, setExpandedJobs] = useState({});
  const [bulkStart, setBulkStart] = useState('');
  const [bulkEnd, setBulkEnd] = useState('');
  const [showBooked, setShowBooked] = useState(true);
  const [dragging, setDragging] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [filterContractor, setFilterContractor] = useState('');
  const timelineRef = React.useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: b }, { data: t }, { data: g }] = await Promise.all([
        api.get('/gantt/appraisals-booked'),
        api.get('/gantt/tasks'),
        api.get('/gantt/gst-due-dates'),
      ]);
      setBookedAppraisals(b || []);
      setTasks(t || []);
      setGstDates(g?.due_dates || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const shiftView = (days) => setViewStart(prev => addDays(prev, days));
  const today = new Date().toISOString().slice(0, 10);

  const dates = useMemo(() => {
    const arr = [];
    for (let i = 0; i < viewDays; i++) arr.push(addDays(viewStart, i));
    return arr;
  }, [viewStart, viewDays]);

  const viewEnd = dates[dates.length - 1] || viewStart;

  const weeks = useMemo(() => {
    const w = [];
    let current = null;
    dates.forEach((d, i) => {
      const weekNum = Math.floor(i / 7);
      if (weekNum !== current) { w.push({ start: i, date: d }); current = weekNum; }
    });
    return w;
  }, [dates]);

  // Unique contractors present in tasks
  const contractorsInSchedule = useMemo(() => {
    const names = new Set();
    tasks.forEach(t => { if (t.contractor_name) names.add(t.contractor_name); });
    return [...names].sort();
  }, [tasks]);

  // Filtered tasks
  const filteredTasks = useMemo(() => {
    if (!filterContractor) return tasks;
    return tasks.filter(t => t.contractor_name === filterContractor);
  }, [tasks, filterContractor]);

  // Group tasks by appraisal
  const jobGroups = useMemo(() => {
    const groups = {};
    filteredTasks.forEach(t => {
      const aid = t.appraisal_id || 'unlinked';
      if (!groups[aid]) groups[aid] = { tasks: [], client_name: t.client_name || '', property_address: t.property_address || '', quote_type: t.quote_type || '', appraisal_status: t.appraisal_status || '', total_cost: 0 };
      groups[aid].tasks.push(t);
      groups[aid].total_cost += (t.total_cost || 0);
    });
    return groups;
  }, [filteredTasks]);

  // Unscheduled tasks
  const unscheduledByJob = useMemo(() => {
    const groups = {};
    filteredTasks.filter(t => !t.scheduled_start && t.status !== 'Completed' && t.status !== 'Declined').forEach(t => {
      const aid = t.appraisal_id || 'unlinked';
      if (!groups[aid]) groups[aid] = { tasks: [], client_name: t.client_name || '', quote_type: t.quote_type || '' };
      groups[aid].tasks.push(t);
    });
    return groups;
  }, [filteredTasks]);

  // Scheduled tasks for timeline
  const scheduledByJob = useMemo(() => {
    const groups = {};
    filteredTasks.filter(t => t.scheduled_start).forEach(t => {
      const aid = t.appraisal_id || 'unlinked';
      if (!groups[aid]) groups[aid] = { tasks: [], client_name: t.client_name || '', property_address: t.property_address || '', quote_type: t.quote_type || '', appraisal_status: t.appraisal_status || '' };
      groups[aid].tasks.push(t);
    });
    return groups;
  }, [filteredTasks]);

  const toggleJob = (id) => setExpandedJobs(prev => ({ ...prev, [id]: !prev[id] }));

  const bulkSchedule = async (appraisalId) => {
    if (!bulkStart) { alert('Pick a start date first'); return; }
    const jobTasks = (jobGroups[appraisalId]?.tasks || []).filter(t => !t.scheduled_start || t.status === 'Draft');
    if (jobTasks.length === 0) { alert('No unscheduled tasks'); return; }
    try {
      await api.post('/gantt/bulk-schedule', {
        task_ids: jobTasks.map(t => t._id),
        scheduled_start: bulkStart,
        scheduled_end: bulkEnd || bulkStart,
      });
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const updateTaskDate = async (taskId, start, end) => {
    try {
      await api.put(`/job-tasks/${taskId}`, { scheduled_start: start, scheduled_end: end || start });
      load();
    } catch { /* ignore */ }
  };

  // Drag handlers for timeline bars
  const handleDragStart = (e, task) => {
    e.dataTransfer.setData('text/plain', task._id);
    setDragging(task._id);
  };

  const handleTimelineDrop = (e, targetDate) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('text/plain');
    if (!taskId || !targetDate) return;
    // Find the task to compute duration offset
    const task = tasks.find(t => t._id === taskId);
    if (!task) return;
    const origStart = task.scheduled_start || targetDate;
    const origEnd = task.scheduled_end || origStart;
    const duration = Math.max(daysBetween(origStart, origEnd), 0);
    const newEnd = addDays(targetDate, duration);
    updateTaskDate(taskId, targetDate, newEnd);
    setDragging(null);
  };

  const handleDragOver = (e) => { e.preventDefault(); };

  // Edge resize handlers
  const handleResizeStart = (e, task, edge) => {
    e.stopPropagation();
    e.preventDefault();
    setResizing({ taskId: task._id, edge, startX: e.clientX, origStart: task.scheduled_start, origEnd: task.scheduled_end || task.scheduled_start });
  };

  useEffect(() => {
    if (!resizing) return;
    const handleMouseMove = (e) => {
      // Calculate how many days the mouse has moved
      const timelineEl = document.querySelector('[data-timeline-area]');
      if (!timelineEl) return;
      const rect = timelineEl.getBoundingClientRect();
      const dayWidth = rect.width / viewDays;
      const deltaX = e.clientX - resizing.startX;
      const deltaDays = Math.round(deltaX / dayWidth);
      if (deltaDays === 0) return;

      setResizing(prev => {
        if (!prev) return null;
        const newState = { ...prev };
        if (prev.edge === 'right') {
          const newEnd = addDays(prev.origEnd, deltaDays);
          if (newEnd >= prev.origStart) newState.previewEnd = newEnd;
        } else {
          const newStart = addDays(prev.origStart, deltaDays);
          if (newStart <= (prev.previewEnd || prev.origEnd)) newState.previewStart = newStart;
        }
        return newState;
      });
    };

    const handleMouseUp = () => {
      if (resizing) {
        const newStart = resizing.previewStart || resizing.origStart;
        const newEnd = resizing.previewEnd || resizing.origEnd;
        if (newStart !== resizing.origStart || newEnd !== resizing.origEnd) {
          updateTaskDate(resizing.taskId, newStart, newEnd);
        }
      }
      setResizing(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing, viewDays]); // eslint-disable-line react-hooks/exhaustive-deps

  const getBarStyleWithResize = (task) => {
    const start = (resizing?.taskId === task._id && resizing.previewStart) || task.scheduled_start;
    const end = (resizing?.taskId === task._id && resizing.previewEnd) || task.scheduled_end || task.scheduled_start;
    if (!start) return null;
    const startIdx = daysBetween(viewStart, start);
    const duration = Math.max(daysBetween(start, end) + 1, 1);
    if (startIdx + duration < 0 || startIdx > viewDays) return null;
    const left = Math.max(startIdx, 0);
    const right = Math.min(startIdx + duration, viewDays);
    const width = right - left;
    return {
      left: `${(left / viewDays) * 100}%`,
      width: `${Math.max((width / viewDays) * 100, 2)}%`,
      backgroundColor: getContractorColor(task.contractor_name),
    };
  };

  // GST markers within view range
  const gstInView = gstDates.filter(g => g.due_date >= viewStart && g.due_date <= viewEnd);

  const getBarStyle = (task) => {
    if (!task.scheduled_start) return null;
    const startIdx = daysBetween(viewStart, task.scheduled_start);
    const endDate = task.scheduled_end || task.scheduled_start;
    const duration = Math.max(daysBetween(task.scheduled_start, endDate) + 1, 1);
    if (startIdx + duration < 0 || startIdx > viewDays) return null;
    const left = Math.max(startIdx, 0);
    const right = Math.min(startIdx + duration, viewDays);
    const width = right - left;
    return {
      left: `${(left / viewDays) * 100}%`,
      width: `${Math.max((width / viewDays) * 100, 2)}%`,
      backgroundColor: getContractorColor(task.contractor_name),
    };
  };

  const todayIdx = daysBetween(viewStart, today);

  if (loading) return <div className="text-center py-12 text-slate-400">Loading schedule...</div>;

  return (
    <div data-testid="job-schedule-page" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Job Schedule</h1>
          <p className="text-sm text-slate-500 mt-1 font-medium"><span className="font-mono-num font-bold text-slate-700">{Object.keys(scheduledByJob).length}</span> jobs scheduled · <span className="font-mono-num font-bold text-slate-700">{Object.values(unscheduledByJob).reduce((s, g) => s + g.tasks.length, 0)}</span> tasks unscheduled</p>
        </div>
        <div className="flex items-center gap-2">
          {contractorsInSchedule.length > 0 && (
            <div className="flex items-center gap-1.5">
              <select data-testid="contractor-filter" value={filterContractor} onChange={e => setFilterContractor(e.target.value)} className="px-3 py-1.5 border border-[#E2E2DF] rounded-md text-sm bg-white">
                <option value="">All Contractors</option>
                {contractorsInSchedule.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {filterContractor && (
                <button data-testid="clear-contractor-filter" onClick={() => setFilterContractor('')} className="px-2.5 py-1.5 text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md hover:bg-rose-100 transition-colors font-semibold">Clear</button>
              )}
            </div>
          )}
          <select value={viewDays} onChange={e => setViewDays(Number(e.target.value))} className="px-3 py-1.5 border border-[#E2E2DF] rounded-md text-sm bg-white">
            <option value={14}>2 Weeks</option>
            <option value={28}>4 Weeks</option>
            <option value={42}>6 Weeks</option>
            <option value={84}>12 Weeks</option>
          </select>
          <button onClick={() => shiftView(-7)} className="p-2 rounded-md border border-[#E2E2DF] hover:bg-slate-50 hover:border-slate-300 transition-colors"><ChevronLeft size={16} /></button>
          <button onClick={() => { const d = new Date(); d.setDate(d.getDate() - 3); setViewStart(d.toISOString().slice(0, 10)); }} className="px-3 py-1.5 border border-[#E2E2DF] rounded-md text-xs font-semibold hover:bg-slate-50 hover:border-slate-300 transition-colors">Today</button>
          <button onClick={() => shiftView(7)} className="p-2 rounded-md border border-[#E2E2DF] hover:bg-slate-50 hover:border-slate-300 transition-colors"><ChevronRight size={16} /></button>
        </div>
      </div>

      {/* Section 1: Appraisals Booked */}
      {bookedAppraisals.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <button onClick={() => setShowBooked(!showBooked)} className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
            <div className="flex items-center gap-2">
              <ClipboardCheck size={16} className="text-blue-600" />
              <span className="text-sm font-semibold text-[#0F172A]">Appraisals Booked</span>
              <span className="text-xs text-slate-400">({bookedAppraisals.length})</span>
            </div>
            {showBooked ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
          </button>
          {showBooked && (
            <div className="border-t border-slate-100">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                  <th className="text-left px-4 py-2">Date</th><th className="text-left px-4 py-2">Time</th>
                  <th className="text-left px-4 py-2">Client</th><th className="text-left px-4 py-2">Property</th>
                  <th className="text-left px-4 py-2">Type</th><th className="text-left px-4 py-2">Appraiser</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {bookedAppraisals.map(a => (
                    <tr key={a._id} data-testid={`booked-appraisal-${a._id}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/appraisals/${a._id}`)}>
                      <td className="px-4 py-2 font-medium text-[#0F172A]">{a.appraisal_date}</td>
                      <td className="px-4 py-2 text-slate-600">{a.appraisal_time || '-'}</td>
                      <td className="px-4 py-2 text-slate-700">{a.client_name}</td>
                      <td className="px-4 py-2 text-slate-500">{a.property_address}</td>
                      <td className="px-4 py-2"><span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">{a.quote_type}</span></td>
                      <td className="px-4 py-2 text-slate-600">{a.appraiser_name || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Main: Timeline + Unscheduled */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Section 2: Gantt Timeline */}
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-xl overflow-hidden">
          {/* Date Header */}
          <div className="border-b border-slate-200">
            <div className="flex">
              <div className="w-52 flex-shrink-0 bg-slate-50 px-3 py-1.5 border-r border-slate-200">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Job / Task</span>
              </div>
              <div className="flex-1 relative">
                <div className="flex">
                  {weeks.map((w, i) => (
                    <div key={i} className="text-center text-[10px] font-semibold text-slate-500 py-1 border-r border-slate-100" style={{ width: `${(7 / viewDays) * 100}%`, minWidth: 0 }}>
                      {new Date(w.date + 'T00:00:00').toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })}
                    </div>
                  ))}
                </div>
                <div className="flex h-4">
                  {dates.map(d => {
                    const isWeekend = [0, 6].includes(new Date(d + 'T00:00:00').getDay());
                    const isToday = d === today;
                    const isGst = gstInView.some(g => g.due_date === d);
                    return <div key={d} className={`border-r ${isWeekend ? 'bg-slate-50' : ''} ${isToday ? 'bg-[#D35400]/10' : ''} ${isGst ? 'bg-red-50' : ''}`} style={{ width: `${(1 / viewDays) * 100}%`, minWidth: 0, borderColor: '#f1f5f9' }}>
                      <span className="text-[7px] text-slate-400 block text-center leading-4">{new Date(d + 'T00:00:00').getDate()}</span>
                    </div>;
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* GST Due Date Markers */}
          {gstInView.length > 0 && (
            <div className="flex border-b border-red-100 bg-red-50/30">
              <div className="w-52 flex-shrink-0 px-3 py-1 border-r border-slate-200 flex items-center gap-1">
                <DollarSign size={11} className="text-red-500" />
                <span className="text-[10px] font-semibold text-red-600">GST Due Dates</span>
              </div>
              <div className="flex-1 relative h-5">
                {gstInView.map((g, i) => {
                  const idx = daysBetween(viewStart, g.due_date);
                  if (idx < 0 || idx >= viewDays) return null;
                  return <div key={i} className="absolute top-0.5 w-3 h-3 rounded-full bg-red-500 border-2 border-white z-10" style={{ left: `${((idx + 0.5) / viewDays) * 100}%`, transform: 'translateX(-50%)' }} title={`GST Return Due: ${g.due_date}`} />;
                })}
              </div>
            </div>
          )}

          {/* Job Rows */}
          {Object.entries(scheduledByJob).length === 0 ? (
            <div className="px-4 py-12 text-center text-slate-400 text-sm">No scheduled tasks yet. Use the panel on the right to schedule tasks.</div>
          ) : (
            Object.entries(scheduledByJob).map(([aid, group]) => {
              const isExpanded = expandedJobs[aid] !== false; // default expanded
              return (
                <div key={aid}>
                  {/* Job Header Row */}
                  <div className="flex border-b border-slate-200 bg-slate-50/50 cursor-pointer" onClick={() => toggleJob(aid)}>
                    <div className="w-52 flex-shrink-0 px-3 py-2 border-r border-slate-200">
                      <div className="flex items-center gap-1">
                        {isExpanded ? <ChevronDown size={12} className="text-slate-400" /> : <ChevronRight size={12} className="text-slate-400" />}
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-[#0F172A] truncate">{group.client_name || 'Unknown'}</p>
                          <p className="text-[10px] text-slate-500 truncate">{group.quote_type} &middot; {group.tasks.length} tasks{group.total_cost > 0 ? ` · $${group.total_cost.toLocaleString(undefined, {minimumFractionDigits: 0})}` : ''}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 relative py-1">
                      {/* Background */}
                      <div className="absolute inset-0 flex">
                        {dates.map(d => <div key={d} className={`border-r ${[0,6].includes(new Date(d+'T00:00:00').getDay()) ? 'bg-slate-50/50' : ''}`} style={{ width: `${(1/viewDays)*100}%`, minWidth:0, borderColor:'#f1f5f9' }} />)}
                      </div>
                      {todayIdx >= 0 && todayIdx < viewDays && <div className="absolute top-0 bottom-0 w-0.5 bg-[#D35400]/30 z-10" style={{ left: `${((todayIdx+0.5)/viewDays)*100}%` }} />}
                      {/* Aggregate bar showing span of all tasks */}
                      {(() => {
                        const starts = group.tasks.filter(t => t.scheduled_start).map(t => t.scheduled_start);
                        const ends = group.tasks.filter(t => t.scheduled_end || t.scheduled_start).map(t => t.scheduled_end || t.scheduled_start);
                        if (starts.length === 0) return null;
                        const minStart = starts.sort()[0];
                        const maxEnd = ends.sort().reverse()[0];
                        const s = daysBetween(viewStart, minStart);
                        const dur = Math.max(daysBetween(minStart, maxEnd) + 1, 1);
                        if (s + dur < 0 || s > viewDays) return null;
                        const l = Math.max(s, 0);
                        const r = Math.min(s + dur, viewDays);
                        return <div className="absolute top-2 h-3 rounded-sm bg-slate-300/50 z-[2]" style={{ left: `${(l/viewDays)*100}%`, width: `${((r-l)/viewDays)*100}%` }} />;
                      })()}
                    </div>
                  </div>

                  {/* Task Rows */}
                  {isExpanded && group.tasks.map(task => {
                    const barStyle = getBarStyleWithResize(task);
                    const isResizingThis = resizing?.taskId === task._id;
                    return (
                      <div key={task._id} data-testid={`gantt-task-${task._id}`} className="flex border-b border-slate-100 hover:bg-slate-50/50 min-h-[32px]">
                        <div className="w-52 flex-shrink-0 px-3 py-1.5 border-r border-slate-200 pl-8">
                          <p className="text-[11px] text-slate-700 truncate">{task.task_name}</p>
                          <p className="text-[9px] text-slate-400 truncate">{task.contractor_name || 'Unassigned'}{task.is_owner_task ? ' (Owner)' : ''}</p>
                        </div>
                        <div className="flex-1 relative py-1" data-timeline-area>
                          <div className="absolute inset-0 flex">
                            {dates.map(d => <div key={d} className={`border-r ${[0,6].includes(new Date(d+'T00:00:00').getDay()) ? 'bg-slate-50/50' : ''}`} style={{ width: `${(1/viewDays)*100}%`, minWidth:0, borderColor:'#f1f5f9' }} onDragOver={handleDragOver} onDrop={e => handleTimelineDrop(e, d)} />)}
                          </div>
                          {todayIdx >= 0 && todayIdx < viewDays && <div className="absolute top-0 bottom-0 w-0.5 bg-[#D35400]/30 z-10" style={{ left: `${((todayIdx+0.5)/viewDays)*100}%` }} />}
                          {barStyle && (
                            <div
                              draggable={!resizing}
                              onDragStart={e => !resizing && handleDragStart(e, task)}
                              className={`absolute top-1 h-5 rounded-sm flex items-center z-[5] group ${resizing ? '' : 'cursor-grab active:cursor-grabbing'} ${dragging === task._id ? 'opacity-50 ring-2 ring-white' : ''} ${isResizingThis ? 'ring-2 ring-white' : ''}`}
                              style={barStyle}
                              title={`${task.task_name} (${task.scheduled_start}${task.scheduled_end && task.scheduled_end !== task.scheduled_start ? ' - '+task.scheduled_end : ''}) — Drag to move, edges to resize`}
                            >
                              {/* Left resize handle */}
                              <div
                                className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize z-10 hover:bg-white/30 rounded-l-sm"
                                onMouseDown={e => handleResizeStart(e, task, 'left')}
                              />
                              <span className="text-[8px] text-white font-semibold truncate px-2 pointer-events-none select-none">{task.task_name.slice(0, 20)}</span>
                              {/* Right resize handle */}
                              <div
                                className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize z-10 hover:bg-white/30 rounded-r-sm"
                                onMouseDown={e => handleResizeStart(e, task, 'right')}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        {/* Section 3: Unscheduled Panel */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
            <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
              <Clock size={14} className="text-amber-500" /> Unscheduled
            </h3>
            <p className="text-[10px] text-slate-500 mt-0.5">{Object.values(unscheduledByJob).reduce((s, g) => s + g.tasks.length, 0)} tasks need dates</p>
          </div>

          {/* Bulk Date Range Picker */}
          <div className="px-4 py-2 border-b border-slate-100 bg-amber-50/50 space-y-1.5">
            <label className="text-[10px] font-medium text-slate-600 block">Bulk Schedule Range</label>
            <div className="flex items-center gap-1.5">
              <input data-testid="bulk-schedule-start" type="date" value={bulkStart} onChange={e => { setBulkStart(e.target.value); if (!bulkEnd || e.target.value > bulkEnd) setBulkEnd(e.target.value); }} className="flex-1 px-2 py-1.5 border border-slate-200 rounded-lg text-xs" />
              <span className="text-[10px] text-slate-400">to</span>
              <input data-testid="bulk-schedule-end" type="date" value={bulkEnd} min={bulkStart} onChange={e => setBulkEnd(e.target.value)} className="flex-1 px-2 py-1.5 border border-slate-200 rounded-lg text-xs" />
            </div>
            {bulkStart && bulkEnd && bulkStart !== bulkEnd && (
              <p className="text-[10px] text-amber-700">{daysBetween(bulkStart, bulkEnd) + 1} day span</p>
            )}
          </div>

          <div className="overflow-y-auto max-h-[500px]">
            {Object.keys(unscheduledByJob).length === 0 ? (
              <div className="px-4 py-8 text-center text-slate-400 text-xs">All tasks are scheduled</div>
            ) : (
              Object.entries(unscheduledByJob).map(([aid, group]) => (
                <div key={aid} className="border-b border-slate-100">
                  <div className="px-4 py-2 bg-slate-50/50 flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-[#0F172A] truncate">{group.client_name || 'Job'}</p>
                      <p className="text-[10px] text-slate-500">{group.quote_type} &middot; {group.tasks.length} tasks</p>
                    </div>
                    <button
                      data-testid={`bulk-schedule-${aid}`}
                      onClick={() => bulkSchedule(aid)}
                      disabled={!bulkStart}
                      className="text-[10px] px-2 py-1 bg-[#D35400] text-white rounded font-medium hover:bg-[#A04000] disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      Schedule All
                    </button>
                  </div>
                  <div className="divide-y divide-slate-50">
                    {group.tasks.map(t => (
                      <div key={t._id} data-testid={`unscheduled-task-${t._id}`} className="px-4 py-1.5 flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] text-slate-700 truncate">{t.task_name}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-[9px] text-slate-400">{t.contractor_name || 'Unassigned'}</span>
                            <span className={`text-[9px] px-1 py-0 rounded-full ${STATUS_BADGE[t.status] || 'bg-slate-100 text-slate-600'}`}>{t.status}</span>
                          </div>
                        </div>
                        <input
                          type="date"
                          className="text-[10px] px-1.5 py-0.5 border border-slate-200 rounded w-28"
                          onChange={e => { if (e.target.value) updateTaskDate(t._id, e.target.value); }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-slate-500">
        {Object.entries(CONTRACTOR_COLORS).map(([name, color]) => (
          <div key={name} className="flex items-center gap-1.5"><div className="w-3 h-3 rounded" style={{ backgroundColor: color }} />{name}</div>
        ))}
        <div className="flex items-center gap-1.5"><div className="w-0.5 h-3 bg-[#D35400]" />Today</div>
        <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-red-500 border-2 border-white" />GST Due</div>
      </div>
    </div>
  );
}
